import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ToastrService } from 'ngx-toastr';
import * as ace from "ace-builds";
import * as langs from '../../../../data/codingEditorlanguages.json';
import { DashboardService } from '../../dashboard.service';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subscription } from 'rxjs';
import { applicationMessages, toasterMessages } from '@messages/*';
import { configureAceSession } from 'src/app/shared/ace-mode-utils';

export interface DialogData {
  _id: string;
  templateName: string;
  templateType: string;
  content: any;
  heading: string;
  update: boolean;
  subject: string;
  templatePlaceholder: string;
  difficultyLevel?: string;
}

export interface Tag {
  name: string;
}

@Component({
  selector: 'app-create-update-coding-ques',
  templateUrl: './create-update-coding-ques.component.html',
  styleUrls: ['./create-update-coding-ques.component.scss']
})

export class CreateUpdateCodingQuesComponent implements OnInit, AfterViewInit {
  @ViewChild("editor") private editor: ElementRef<HTMLElement>;
  @ViewChild("verifyCodeEditor") private verifyCodeEditor: ElementRef<HTMLElement>;
  @ViewChild('questionsTabGroup') tabGroup;
  @Input() questionResponse: any;
  @Output() createUpdateCQ = new EventEmitter<string>();
  dropdownSelctedLangs: any[];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  tags: Tag[] = [];
  activeTabIndex = 0;
  options = {
    autoRefresh: true,
    fixedGutter: true,
    indentWithTabs: true,
    lineNumbers: true,
    lineWrapping: true,
    lint: true,
    smartIndent: false,
    theme: "material",
    mode: 'javascript'
  }

  public topics = [];
  returnTypes: any = [];
  initialCodeAceEditor: any;
  verifyCodeAceEditor: any;
  testparamsForm = this.fb.array([]);
  createCodingQuesFrom: UntypedFormGroup;
  difficultyLevels = [{ id: 'LOW', name: 'Easy' }, { id: 'MEDIUM', name: 'Medium' }, { id: 'HIGH', name: 'Hard' }];
  minutes: number[] = [];
  hours: number[] = [0, 1, 2, 3, 4, 5];
  codingEditorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '0',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter question here...',
    defaultParagraphSeparator: '',
    defaultFontName: '',
    defaultFontSize: '',
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyLeft',
        'justifyCenter',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName'
      ],
      [
        'customClasses',
        'unlink',
        'insertVideo',
        'removeFormat',
        'insertHorizontalRule'
      ]
    ]
  };

  topicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'topic',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true
  };

  numberTypes = {
    'Integer': 'Integer',
    'integer': 'integer',
    'int': 'int',
    'Float': 'Float',
    'float': 'float',
    'Double': 'Double',
    'double': 'double',
    'Number': 'Number',
    'number': 'number'
  };

  codingLanguages: any = JSON.parse(JSON.stringify((langs as any).default));
  langCode: any;
  methodWithParams = "";
  runCompleted: boolean = false;
  tokesList: any;
  runTestResult: any;
  private selectedLang: any;
  private editObj: any;
  private previousInitialValue: string = '';
  public isNewLangQuestion: boolean = true;
  private subscriptions: Subscription[] = [];

  constructor(public dialogRef: MatDialogRef<CreateUpdateCodingQuesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dashboardService: DashboardService,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    private toastr: ToastrService
  ) {
    this.initializeTheForm();
  }

  ngOnInit(): void {
    this.getTopics();
    this.minutes = [];
    for (let index = 0; index < 60; index++) {
      this.minutes.push(index);
    }
    this.patchValues();
  }

  ngAfterViewInit(): void {
    this.activeTabIndex = this.tabGroup.selectedIndex;
    this.setEditorOnAfterLViewInit();
    setTimeout(() => {
      if (this.data.update) {
        this.dropdownSelctedLangs = this.codingLanguages.filter((item1) => {
          return this.editObj.questionSignatures.find(item2 => item2.functionSignature.language_id === item1.id)
        });
        this.languageSelect({ value: this.dropdownSelctedLangs[0], idx: 0 });
      }
    }, 0);
  }

  get selctedLanguages() {
    return this.createCodingQuesFrom?.controls['languages']?.value;
  }

  get params(): UntypedFormArray {
    return this.createCodingQuesFrom?.get('functionSignature')?.get('code_params') as UntypedFormArray;
  }

  get functionSignatureControls() {
    return this.createCodingQuesFrom?.get('functionSignature');
  }

  get testCases(): UntypedFormArray {
    return this.createCodingQuesFrom?.get("testCases") as UntypedFormArray;
  }

  /**
   * method used to initialize the create coding questions form
   * set default values to form
   */
  initializeTheForm() {
    this.createCodingQuesFrom = this.fb.group({
      hourLimit: [0],
      minLimit: [15, [Validators.required]],
      questionType: ['CODING', [Validators.required]],
      questionText: ['', [Validators.required]],
      answer: [''],
      topics: [[]],
      difficultyLevel: [this.data?.difficultyLevel || '', [Validators.required]],
      tags: [[]],
      languages: [[], [Validators.required]],
      functionSignature: this.fb.group({
        language_id: ['', [Validators.required]],
        source_code: [''],
        stdin: ['testInput'],
        code_language: [''],
        code_language_name: [''],
        code_params: this.fb.array([]),
        function_name: ['', [Validators.required]],
        return_type: ['', [Validators.required]],
        type: ['CODING']
      }),
      initialCode: ['', [Validators.required]],
      testCases: this.fb.array([])
    });
    //testCases: this.fb.array(this.initialTestCases())

    this.initialParams();
    if (!this.data.update) {
      this.addInitialTestCases(1);
    }
  }

  setEditorOnAfterLViewInit() {
    ace.config.set("fontSize", "14px");
    ace.config.set('basePath', 'https://unpkg.com/ace-builds@1.4.12/src-noconflict');
    this.initialCodeAceEditor = ace.edit(this.editor.nativeElement);
    this.initialCodeAceEditor.session.setValue("<h1>Coding Editor works great in Evaltrix!</h1>");
    this.initialCodeAceEditor.setTheme('ace/theme/solarized_dark');
    this.verifyCodeAceEditor = ace.edit(this.verifyCodeEditor.nativeElement);
    this.verifyCodeAceEditor.session.setValue("<h1>Coding Editor works great in Evaltrix!</h1>");
    this.verifyCodeAceEditor.setTheme('ace/theme/solarized_dark');
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * params are used to show on Tab1 for edit and new test cases
   */
  initialParams() {
    if (this.data.update) {
      const paramsEditRefData = this.data?.content;
      for (var piData = 0; piData < paramsEditRefData.questionSignatures[0].functionSignature.code_params.length; piData++) {
        this.params.push(this.fb.group({
          param: ['', [Validators.required]],
          type: ['', [Validators.required]]
        }));
      }
    } else {
      this.params.push(this.fb.group({
        param: ['', [Validators.required]],
        type: ['', [Validators.required]]
      }))
    }
  }

  /**
   * To add inital params for add /edit coding-question
   */
  addParam() {
    this.params.push(this.fb.group({
      param: ['', [Validators.required]],
      type: ['', [Validators.required]]
    }));

    const testCasesCotrols = this.createCodingQuesFrom?.controls['testCases']['controls'];
    for (var i = 0; i < testCasesCotrols?.length; i++) {
      const testParams = <UntypedFormArray>testCasesCotrols[i].get('testParams');
      let testParamGroup = this.fb.group({
        testParamValue: [null, Validators.required],
      });
      testParams?.push(testParamGroup);
    }
  }

  /**
   * this is used when we add new test cases and 
   * we need add params for that test case
   * @param index 
   */
  addTestParams(index: number) {
    let testparamsLength = this.createCodingQuesFrom?.value?.functionSignature?.code_params?.length;
    this.testparamsForm = this.fb.array([]);
    this.testparamsForm = this.createCodingQuesFrom?.controls['testCases']['controls'][index]?.get('testParams');

    if (testparamsLength !== this.testparamsForm?.value?.length) {
      for (let index = 0; index < testparamsLength; index++) {
        let testParam = this.fb.group({
          testParamValue: [null, Validators.required],
        })
        this.testparamsForm.push(testParam);
      }
    }

  }

  removeParam(idx: number): void {
    const testCasesCotrols = this.createCodingQuesFrom?.controls['testCases']['controls'];
    for (var i = 0; i < testCasesCotrols?.length; i++) {
      const testParams = <UntypedFormArray>testCasesCotrols[i].get('testParams');
      testParams?.removeAt(idx);
    }
    this.params?.removeAt(idx);
  }

  /**
   * Convert escaped newlines (\n as text) to actual newlines
   */
  private convertEscapedNewlines(code: string): string {
    if (!code) return '';
    return code.replace(/\\n/g, '\n');
  }

  /**
   * On Page load for new and edit location to set Inital test cases & test params
   */
  addInitialTestCases(noOfTestCases) {
    let testCasesLength = 2;
    (this.createCodingQuesFrom.controls['testCases'] as UntypedFormArray).clear();
    if (this.data?.update) {
      testCasesLength = noOfTestCases + 1;
    }
    for (let index = 1; index < testCasesLength; index++) {
      let testCaseObj = this.fb.group({
        name: ['Test' + index, Validators.required],
        output: ['', Validators.required],
        type: [null],
        testParams: this.fb.array([])
      });
      let testParamsArray = testCaseObj?.get('testParams') as UntypedFormArray;
      //to get the paramters from function signature params
      const testParamsArrayRef = this.createCodingQuesFrom?.value?.functionSignature?.code_params;
      if (this.data?.update) {
        for (var i = 0; i < testParamsArrayRef?.length; i++) {
          testParamsArray.push(this.fb.group({
            testParamValue: [null, Validators.required],
          }));
        }
      } else {
        testParamsArray.push(this.fb.group({
          testParamValue: [null, Validators.required],
        }));
      }
      this.testCases.push(testCaseObj)
    }
  }

  /**
   * on click of add test cases will add new test cases to array
   */
  addNewCase() {
    let length = this.testCases?.length + 1 || 1;
    const testCaseForm = this.fb.group({
      name: ['Test' + length, Validators.required],
      output: ['', Validators.required],
      type: [null],
      testParams: this.fb.array([])
    });
    this.testCases.push(testCaseForm);
    //add test params for that test cases  only
    this.addTestParams(length - 1);
  }

  deleteTestCase(caseIndex: number) {
    this.testCases.removeAt(caseIndex);
  }

  setDataOnEdit(langId) {
    let currentObj = this.editObj.questionSignatures.find(item => item.functionSignature.language_id === langId);
    let editFunctionSignature = currentObj.functionSignature;
    let questionTestCases = currentObj.testCases;
    let currentLangIdx = this.dropdownSelctedLangs?.findIndex(obj => obj.id == langId);
    this.addInitialTestCases(questionTestCases.length);
    this.langCode = { id: editFunctionSignature.language_id };
    this.returnTypes = this.dropdownSelctedLangs[currentLangIdx].returnTypes;
    this.initialCodeAceEditor.session.setValue(this.convertEscapedNewlines(currentObj.initialCode));

    this.createCodingQuesFrom.patchValue({
      functionSignature: {
        language_id: editFunctionSignature.language_id,
        source_code: editFunctionSignature.source_code,
        code_language_name: editFunctionSignature.code_language_name,
        stdin: 'testInput',
        code_language: editFunctionSignature.code_language,
        code_params: editFunctionSignature.code_params,
        function_name: editFunctionSignature.function_name,
        return_type: editFunctionSignature.return_type
      },
      testCases: questionTestCases
    });
    this.verifyCodeAceEditor.session.setValue(this.convertEscapedNewlines(editFunctionSignature.source_code));
    this.tabChanged({ index: 0 });
    this.runTestResult = currentObj?.run_results || {};
    this.runCompleted = true;
  }

  addOrUpdateQuestion(): void {
    this.createCodingQuesFrom.patchValue({ answer: this.verifyCodeAceEditor?.getValue() })
    let createQuestionReqObj = {
      update: this.data?.update,
      _id: this.data?.content._id,
      run_results: this.runTestResult || {},
      ...this.createCodingQuesFrom.value
    }
    this.createUpdateCQ.emit(createQuestionReqObj);
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.tags.push({ name: value });
    }
    event.input.value = '';
  }

  removeTag(tag: Tag): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }

  tabChanged(tab): void {
    this.activeTabIndex = tab.index;
    let functionname = this.createCodingQuesFrom.value.functionSignature.function_name || 'undefined';
    if (this.activeTabIndex === 1 || this.activeTabIndex === 3) {
      //Initial code logic here.
      let initialValue = ``;
      let returnType = this.createCodingQuesFrom.value.functionSignature.return_type || 'void';
      let functionParams = ``;
      let separaters = ``;
      this.createCodingQuesFrom?.value?.functionSignature?.code_params.map((item, index) => {
        let separater = '';
        if (index != this.createCodingQuesFrom?.value?.functionSignature?.code_params.length - 1) {
          separater = ', '
        }
        if (item.param === '') {
          item.param = 'undefined';
        }
        if (item.type === '') {
          item.type = 'undefined';
        }
        switch (this.options.mode) {
          case 'javascript':
            functionParams += `${item.param}${separater}`;
            initialValue = `function ${functionname}(${functionParams}) {
  //Insert your code here
}`
            break;
          case 'typescript':
            functionParams += `${item.param}:${item.type}${separater}`;
            initialValue = `export default function ${functionname}(${functionParams}) : ${returnType} {
 //Insert your code here
}`
            break;
          case 'python':
            functionParams += `${item.param}${separater}`;
            initialValue = `
def ${functionname}( ${functionParams} ) :
 #Insert your code here
`
            break;
          case 'php':
            functionParams += `$${item.param}${separater}`;
            initialValue = `<?php function ${functionname}( ${functionParams} ) {
 //Insert your code here
}
?>`
            break;
          case 'java':
            functionParams += `${item.type} ${item.param} ${separater}`;
            initialValue = `public static ${returnType} ${functionname}( ${functionParams} ) {
 //Insert your code here
}`
            break;
          case 'kotlin':
            functionParams += `${item.param} : ${item.type} ${separater}`;
            initialValue = `fun ${functionname}( ${functionParams} ) : ${returnType} {
 //Insert your code here
}`
            break;
          case 'c_cpp':
            functionParams += `${item.type} ${item.param}${separater}`;
            initialValue = `${returnType} ${functionname}(${functionParams}) {
 //Insert your code here
}`
            break;
          case 'c++':
            functionParams += `${item.type} ${item.param}  ${separater}`;
            initialValue = `${returnType} ${functionname}( ${functionParams} )  {
 //Insert your code here
}`
            break;
          case 'csharp':
            functionParams += `${item.type} ${item.param}  ${separater}`;
            initialValue = `using System;
using System.Linq;
using TestTaker;
namespace TestTaker
{
  public  class CustomCode
  {
    public  ${returnType} ${functionname} (${functionParams}) {
      //Insert your code here
    }
  }
}`
            break;
          case 'ruby':
            functionParams += `${item.param}${separater}`;
            initialValue = `def ${functionname}( ${functionParams} ) 
 #Insert your code here
end`
            break;
          case 'golang':
            functionParams += `${item.param} ${item.type}${separater}`;
            initialValue = `import()
func ${functionname}( ${functionParams} ) ${returnType} {
  //Insert your code here
}`
            break;
          case 'r':
            functionParams += `${item.param}${separater}`;
            initialValue = `${functionname} <- function ( ${functionParams} ) {
  #Insert your code here
}`
            break;
          case 'swift':
            functionParams += `_ ${item.param}:${item.type}${separater}`;
            initialValue = `func ${functionname}( ${functionParams} ) -> ${returnType} {
  //Insert your code here
}`
            break;
          case 'scala':
            functionParams += `${item.param}:${item.type}${separater}`;
            initialValue = `def ${functionname}( ${functionParams} ) : ${returnType} = {
  //Insert your code here
}`
            break;
          default:
            break;
        }
        separaters += `${separater}`;
        this.methodWithParams = `${functionname}( ${separaters} ) => `

      })

      this.createCodingQuesFrom.patchValue({
        initialCode: initialValue
      });

      this.initialCodeAceEditor.session.setValue(this.convertEscapedNewlines(initialValue));
      if (this.activeTabIndex === 3) {
        this.updateFinalCode({ initialValue, ...tab });
      }

    }
  }

  updateFinalCode(obj: any) {
    if ((!this.editObj && this.previousInitialValue !== obj.initialValue) || this.isNewLangQuestion) {
      this.previousInitialValue = obj.initialValue;
      this.verifyCodeAceEditor.session.setValue(this.convertEscapedNewlines(obj.initialValue));
    }
  }

  resetOnCodingLanguageChange(): void {
    //this.initializeTheForm();
  }

  getMethodWithArguments(index: number) {
    if (this.activeTabIndex === 2) {
      let functionname = this.createCodingQuesFrom?.value?.functionSignature?.function_name || 'undefined';
      const testCase = this.createCodingQuesFrom?.value?.testCases[index];
      let separaters = '';
      const testcaseOutput = (testCase?.output) ? testCase?.output : '';
      if (testCase?.testParams) {
        const paramsValues = testCase?.testParams?.map((item) => item?.testParamValue);
        separaters = paramsValues.filter(Boolean).join(", ");
      }
      return `${functionname}( ${separaters} ) => ${testcaseOutput}`
    }
  }

  runTestCases() {
    const codingLanguageId = this.createCodingQuesFrom?.value?.functionSignature?.language_id;
    this.createCodingQuesFrom?.value?.testCases?.map(val => val.type = this.createCodingQuesFrom?.value?.functionSignature?.return_type)
    let reqData = {
      language_id: codingLanguageId,
      source_code: this.convertEscapedNewlines(this.verifyCodeAceEditor?.getValue()),
      code_test_cases: this.createCodingQuesFrom?.value?.testCases,
      function_name: this.createCodingQuesFrom?.value?.functionSignature?.function_name,
      initial_code: this.convertEscapedNewlines(this.initialCodeAceEditor?.getValue()),
      test_params: this.createCodingQuesFrom?.value?.functionSignature?.code_params
    };

    this.subscriptions.push(
      this.dashboardService.runTests(reqData).subscribe((res) => {
        this.runTestResult = res;
        this.runCompleted = true;
      }, (error) => {
        this.runTestResult = error;
      })
    );

  }

  gotoNextStep(): void {
    this.activeTabIndex += 1;
  }

  onLanguageSelection(event: any) {
    let selected = [];
    let unselected = [];
    this.codingLanguages.forEach(item => {
      if (event.value.indexOf(item) !== -1) {
        selected.push(item);
      } else {
        unselected.push(item)
      }
    })
    this.codingLanguages = [...selected, ...unselected];
  }

  /**
   *
   * @param lang 
   * when language selcted from selcted language
   * reset data if new
   * update/prepopulate if it is having data
   */
  languageSelect(lang): void {
    let questionExist = false;
    this.questionResponse = this.editObj || this.questionResponse;
    if (this.data?.update) {
      for (let i = 0; i < this.questionResponse?.questionSignatures?.length; i++) {
        if (this.questionResponse.questionSignatures[i].functionSignature.language_id === lang.value.id) {
          questionExist = true;
        }
        let currentRef = this.dropdownSelctedLangs.find((item) => this.questionResponse.questionSignatures[i].functionSignature.language_id === item.id);
        if (currentRef) {
          currentRef.isQuestionAdded = true;
        }

      }
    }
    this.options.mode = lang.value.value;
    configureAceSession(this.initialCodeAceEditor.session, lang.value.value);
    configureAceSession(this.verifyCodeAceEditor.session, lang.value.value);
    if (this.data?.update && questionExist) {
      if (this.questionResponse) {
        this.editObj = this.questionResponse;
      }
      this.setDataOnEdit(lang.value.id);
      this.isNewLangQuestion = false;
    } else {
      this.isNewLangQuestion = true;
      this.resetFilledData(lang);
    }
  }

  resetFilledData(currentLanguage) {
    if (!this.data.update) {
      this.createCodingQuesFrom.controls['functionSignature']['controls']['function_name'].reset();
    }
    this.createCodingQuesFrom.controls['functionSignature']['controls']['code_language'].reset();
    this.createCodingQuesFrom.controls['functionSignature']['controls']['language_id'].reset();
    this.createCodingQuesFrom.controls['functionSignature']['controls']['return_type'].reset();
    this.returnTypes = [];
    if (!this.data.update) {
      this.removeParamsAndTestCases();
      this.initialParams();
    }
    if (this.data.update && this.isNewLangQuestion) {
      let firstLanguageObj = this.questionResponse.questionSignatures[0];
      this.addInitialTestCases(firstLanguageObj?.testCases?.length);
    } else {
      this.addInitialTestCases(1);
    }
    this.langCode = currentLanguage.value;
    this.returnTypes = [...currentLanguage.value.returnTypes];
    this.createCodingQuesFrom.patchValue({
      functionSignature: {
        code_language: currentLanguage?.value?.value,
        code_language_name: currentLanguage?.value?.name,
        language_id: currentLanguage?.value?.id
      },
      testCases: this.editObj?.questionSignatures[0]?.testCases || []
    });
    this.tabChanged({ index: 0 });
    this.runTestResult = {};
    this.runCompleted = false;
  }

  /**
   * remove test params and test cases on language selection change
   */
  removeParamsAndTestCases() {
    this.removeAllTestCases(0);
    this.removedAllTestParams(0);
  }

  removeAllTestCases(index: number) {
    if (this.testCases?.length) {
      this.deleteTestCase(index);
      this.removeAllTestCases(index++);
    }
  }

  removedAllTestParams(index: number) {
    if (this.params?.length) {
      this.params?.removeAt(index);
      this.removedAllTestParams(index++);
    }
  }

  deleteSelectedLanguage(deleteLangRef) {
    if (deleteLangRef?.isQuestionAdded) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'delete-dialog-container',
        data: {
          danger: true,
          messageBody: applicationMessages.questionLangDeleteConfirm
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          const payload = { lanuagageId: deleteLangRef?.id };
          const questionId = this?.data?.content?._id;
          this.dashboardService.deleteLanguagesFromCQ(payload, questionId).subscribe(() => {
            this.deleteQuestionOnDemand(deleteLangRef);
            this.toastr.success(toasterMessages.questionLangDeleteSuccess.replace('{deleteLangRef}', `${deleteLangRef?.name}`));
            // TODO get the latest results.
          }, (error) => {
            this.toastr.error(error?.error?.errors[0].error, 'Error');
          })

        }
      });
    } else {
      this.deleteQuestionOnDemand(deleteLangRef);
    }
  }


  deleteQuestionOnDemand(deleteLangRef) {
    if (this.langCode?.id === deleteLangRef?.id) {
      this.langCode = {};
    }
    const filtredValues = this.dropdownSelctedLangs?.filter((item: any) => item?.id !== deleteLangRef?.id) || [];
    this.dropdownSelctedLangs = filtredValues;
    if (this.dropdownSelctedLangs?.length > 0) {
      this.languageSelect({ value: this.dropdownSelctedLangs[0], idx: 0 });
    }
  }

  getFormValid() {
    return this.createCodingQuesFrom.valid
  }

  private patchValues(): void {
    if (this.data.update) {
      this.editObj = this.data.content;
      let topics = [];
      this.topics.find(item => {
        this.editObj.topics.map((id: any) => {
          if (id === item._id) {
            topics.push(item);
          }
        })
      });
      this.tags = this.editObj.tags.map(item => {
        return { name: item };
      });
      this.createCodingQuesFrom.patchValue({
        hourLimit: this.editObj.hourLimit,
        minLimit: this.editObj.minLimit,
        questionType: this.editObj.questionType,
        questionText: this.editObj.questionText,
        answer: this.editObj.answer,
        topics: topics,
        difficultyLevel: this.editObj.difficultyLevel,
        initialCode: this.editObj.initialCode
      });

      this.selectedLang = this.codingLanguages.find(obj => {
        return obj.id === this.editObj.questionSignatures[0].functionSignature.language_id
      })
    }
  }

  private getTopics(): void {
    const params = { pageNo: 1, limit: 20, searchWord: '' };
    this.dashboardService.getEnterpriseTopics(params).subscribe((response: any) => {
      this.topics = response.topics || [];
    });
  }

  minuteOrHourLimitChange(min): void {
    if (!this.createCodingQuesFrom.value.hourLimit && !this.createCodingQuesFrom.value.minLimit) {
      this.createCodingQuesFrom.controls['minLimit'].setErrors({ 'incorrect': true });
    } else {
      this.createCodingQuesFrom.controls['minLimit'].setErrors(null);
    }
  }

  openCloseLangSelect(event): void {
    if (!event) {
      this.languageSelect({ value: this.dropdownSelctedLangs[0], idx: 0 });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }


}